<template>
  <TeamPersonRow :name="invite.email" :details="details" :badges="badges">
    <template #leading>
      <Avatar :user-name="invite.email" show-single-letter status="pending" size="medium" />
    </template>

    <template #action>
      <View gap="sm">
        <Button :variant="invite.expired ? 'primary' : 'outline'" size="small" :loading="isResending" @click="resend">
          Resend
        </Button>
        <Button variant="ghost" size="small" @click="emit('revoke')">Revoke</Button>
      </View>
    </template>
  </TeamPersonRow>
</template>

<script setup lang="ts">
import { formatDistanceToNowStrict } from 'date-fns';
import { computed, ref } from 'vue';

import { Avatar, Button, View, useAlertsStore } from '@tallo/design-system';
import {
  PendingInviteDto,
  companyRoleBadgeVariants,
  companyRoleLabels,
  useResendInvite,
} from '@tallo/investor/company';
import { TeamPersonRow, TeamRoleBadge } from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

const props = defineProps<{ invite: PendingInviteDto }>();
const emit = defineEmits<{ revoke: [] }>();

const alertsStore = useAlertsStore();
const resendInvite = useResendInvite();
// Tracked per row: the mutation is shared by every pending row, so its own loading flag would spin them all
const isResending = ref(false);

const badges = computed<TeamRoleBadge[]>(() => [
  { label: companyRoleLabels[props.invite.role], variant: companyRoleBadgeVariants[props.invite.role] },
  ...(props.invite.rejoining ? [{ label: 'Rejoining', variant: 'blue' as const }] : []),
  ...(props.invite.expired ? [{ label: 'Expired', variant: 'yellow' as const }] : []),
]);

const details = computed(() => {
  const expiry = props.invite.expired
    ? 'the link has expired'
    : `expires ${formatDistanceToNowStrict(new Date(props.invite.expiresAt), { addSuffix: true })}`;

  // A backend that predates invite portfolios sends none
  const joins = (props.invite.collaboratePortfolios ?? []).map((portfolio) => portfolio.name);

  return [
    `Invited by ${props.invite.invitedByName || 'an admin'}`,
    expiry,
    ...(joins.length ? [`joins ${joins.join(', ')}`] : []),
  ].join(' · ');
});

async function resend() {
  isResending.value = true;

  try {
    await resendInvite.mutateAsync(props.invite.id);
    alertsStore.success(`Invite resent to <b>${escapeHtml(props.invite.email)}</b>`);
  } catch {
    // Already reported by the mutation's toast
  } finally {
    isResending.value = false;
  }
}
</script>
